/**
 * Reads the current Cognito **access token** from the Amplify session.
 *
 * Bearer-token APIs (the agent `/agents` endpoint) want the access token —
 * its `client_id` + `scope` claims drive the AgentCore JWT authorizer. This is
 * the bearer-token sibling of `signedFetch` (which uses Identity Pool IAM
 * credentials for SigV4 routes). Throws when no signed-in session exists.
 *
 * The access token lives ~60 min (the web client's `accessTokenValidity`); the
 * refresh token ~30 days. Amplify normally refreshes silently, but a token
 * cached right at its expiry boundary can still reach the agent's authorizer as
 * expired → a 401. So we proactively force a refresh when the cached token is
 * within a small skew of expiry, using the long-lived refresh token, so the
 * common "hourly access token rolled over" case never surfaces as an error.
 */

import { fetchAuthSession } from 'aws-amplify/auth';

/** Refresh this many seconds before the access token's `exp` to absorb skew. */
const EXPIRY_SKEW_SECONDS = 30;

function expiresWithin(
  token: { payload?: { exp?: number } },
  skewSeconds: number,
): boolean {
  const exp = token.payload?.exp;
  if (typeof exp !== 'number') {
    return false;
  }
  return Date.now() / 1000 >= exp - skewSeconds;
}

export async function getAccessToken(): Promise<string> {
  let session = await fetchAuthSession();
  let token = session.tokens?.accessToken;
  // Missing or about-to-expire → force a refresh off the refresh token. If the
  // refresh token is also dead, this throws / yields no token, which the caller
  // (streamAgentTurn) treats as an expired session and routes to sign-in.
  if (!token || expiresWithin(token, EXPIRY_SKEW_SECONDS)) {
    session = await fetchAuthSession({ forceRefresh: true });
    token = session.tokens?.accessToken;
  }
  if (!token) {
    throw new Error('Not signed in — no Cognito access token available');
  }
  return token.toString();
}
